import { createContext, useContext, useRef, useState } from "react"
import { CatalogItem } from "../../domain/catalogItem"
import { OverlayItem } from "./ItemOverlay"
import CatalogBrowserDndContext from "./CatalogBrowserDndContext"
import { CatalogTemplateWithItems } from "../../domain/catalogTemplate"

export type AddCatalogItem = (catalogItem: CatalogItem, toId?: string, selectedWorkareaId?: number, name?: string) => void
export type AddTemplate = (template: CatalogTemplateWithItems, toGuid?: string) => void
export type UpdateSorting = (fromId: string, toId: string) => void
export type FindOverlayItem = (itemId: string) => OverlayItem | undefined
export type GetViewItemForDraggableCatalogItem = (catalogItem: CatalogItem, dragId: string) => any
export type GetViewItemForDraggableTemplate = (template: CatalogTemplateWithItems, dragId: string) => any

export type ItemHandlerRef = {
  addCatalogItem: AddCatalogItem
  addTemplate: AddTemplate
  updateSorting: UpdateSorting
  findOverlayItem: FindOverlayItem
  getViewItemForDraggableCatalogItem: GetViewItemForDraggableCatalogItem
  getViewItemForDraggableTemplate: GetViewItemForDraggableTemplate
}

type CatalogBrowserContextData = {
  addCatalogItem: AddCatalogItem
  addTemplate?: AddTemplate
  allowDragging: boolean
  isCatalogBrowserOpen: boolean
  toggleCatalogBrowser: () => void
  itemHandlerRef: React.MutableRefObject<undefined>
}

const CatalogBrowserContext = createContext<CatalogBrowserContextData>({
  addCatalogItem: () => { },
  isCatalogBrowserOpen: false,
  allowDragging: false,
  toggleCatalogBrowser: () => { },
  itemHandlerRef: {current: undefined}
})

export default CatalogBrowserContext

type Props = React.PropsWithChildren & {
  allowDragging: boolean
  showTemplates: boolean
}

export const CatalogBrowserContextWrapper = ({allowDragging, showTemplates, children}: Props) => {
  const [isCatalogBrowserOpen, setIsCatalogBrowserOpen] = useState(false)
  const itemHandlerRef = useRef();

  const toggleCatalogBrowser = () => {
    setIsCatalogBrowserOpen(!isCatalogBrowserOpen)
  }

  const currentRef = (): ItemHandlerRef | undefined => {
    if (itemHandlerRef.current) {
      return itemHandlerRef.current as unknown as ItemHandlerRef
    }
  }

  const addCatalogItem = (catalogItem: CatalogItem, toId?: string, selectedWorkareaId?: number, name?: string) => {
    currentRef()?.addCatalogItem(catalogItem, toId, selectedWorkareaId, name)
  }

  const addTemplate = (template: CatalogTemplateWithItems, toId?: string) => {
    currentRef()?.addTemplate(template, toId)
  }

  return <CatalogBrowserContext.Provider value={{
      addCatalogItem,
      addTemplate: showTemplates ? addTemplate : undefined,
      allowDragging,
      isCatalogBrowserOpen,
      toggleCatalogBrowser,
      itemHandlerRef
    }}>
    <CatalogBrowserDndContext
      addCatalogItem={addCatalogItem}
      addTemplate={addTemplate}
      updateSorting={(fromId, toId) => currentRef()?.updateSorting(fromId, toId)}
      findOverlayItem={id => currentRef()?.findOverlayItem(id)}
      getViewItemForDraggableCatalogItem={(catalogItem, dragId) => currentRef()?.getViewItemForDraggableCatalogItem(catalogItem, dragId)}
      getViewItemForDraggableTemplate={(template, dragId) => currentRef()?.getViewItemForDraggableTemplate(template, dragId)}
    >
      {children}
    </CatalogBrowserDndContext>
  </CatalogBrowserContext.Provider>
}
export type EditorSidebarTabKey = 'list' | 'item-catalog'

export const useEditorSidebarTabKey = (): EditorSidebarTabKey => {
  const { isCatalogBrowserOpen } = useContext(CatalogBrowserContext)
  return isCatalogBrowserOpen ? 'item-catalog' : 'list'
}
